import { Routes } from '@angular/router';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { LoginComponent } from './pages/login/login.component';
import { authGuard } from './core/guards/msal-guard';
import { redirectToDashboardGuard } from './core/guards/redirect-todashboard.guard';
import { RoleGuardGuard } from './core/guards/role-guard.guard';
import { UnauthorizedComponent } from './pages/unauthorized/unauthorized.component';
import { ForbiddenComponent } from './pages/forbidden/forbidden.component';
import { LandingPageComponent } from './pages/landing-page/landing-page.component';
import { getPath, PathUnit } from './consts/path.const';
import { AllAppLinks, AppLink } from './consts/app-links.config';
import { UnsubscribeEmailNotifyComponent } from './pages/unsubscribe-email-notify/unsubscribe-email-notify.component';
import { environment } from '@/environments/environment';
import { Route } from '@angular/router';

// Component mapping for routes
export const componentMap: Record<string, Pick<Route, 'loadComponent'>> = {
  [getPath(PathUnit.AskBot)]: {
    loadComponent: () => import('./pages/ask-bot/ask-bot.component').then(m => m.AskBotComponent),
  },
  [getPath(PathUnit.PreRequisite)]: {
    loadComponent: () => import('./pages/pre-requisite/pre-requisite.component').then(m => m.PreRequisiteComponent),
  },
  [getPath(PathUnit.NodeValidator)]: {
    loadComponent: () => import('./pages/node-validator/node-validator.component').then(m => m.NodeValidatorComponent),
  },
  // Job Lookup 路由：使用原来的 JobLookup 路径，但加载 JobStatusComponent
  [getPath(PathUnit.JobLookup)]: {
    loadComponent: () => import('./pages/job-status/job-status.component').then(m => m.JobStatusComponent),
  },
  [getPath(PathUnit.SoftwareCenter)]: {
    loadComponent: () => import('./pages/software-center/software-center.component').then(m => m.SoftwareCenterComponent),
  },
  [getPath(PathUnit.LiveMonitoring)]: {
    loadComponent: environment.production && environment.region === 'com' ? () => import('./pages/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent) 
    : () => import('./pages/live-monitoring/live-monitoring.component').then(m => m.LiveMonitoringComponent),
  },
  [getPath(PathUnit.NotificationsMgmt)]: {
    loadComponent: () => import('./pages/notifications-mgmt/notifications-mgmt.component').then(m => m.NotificationsMgmtComponent),
  },
  [getPath(PathUnit.SystemOwnersMgmt)]: {
    loadComponent: () => import('./pages/system-owners/system-owners.component').then(m => m.SystemOwnersComponent),
  },
  [getPath(PathUnit.RequestAccess)]: {
    loadComponent: () => import('./pages/request-access/request-access.component').then(m => m.RequestAccessComponent),
  },
  // [getPath(PathUnit.RoleValidator)]: {
  //   loadComponent: () => import('./pages/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent),
  // },
  // [getPath(PathUnit.ComingSoon)]: {
  //   loadComponent: () => import('./pages/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent),
  // },
  [getPath(PathUnit.SystemSettings)]: {
    loadComponent: () => import('./pages/settings/settings.component').then(m => m.SettingsComponent),
  },
  [getPath(PathUnit.SoftwareCenterUpload)]: {
    loadComponent: () => import('./pages/software-center/pages/upload-page/upload-page.component').then(m => m.UploadPageComponent),
  },
  // [getPath(PathUnit.Unsubscribe)]: {
  //   loadComponent: () => import('./pages/unsubscribe-email-notify/unsubscribe-email-notify.component').then(m => m.UnsubscribeEmailNotifyComponent),
  // },
  [getPath(PathUnit.AIContentManager)]: {
    loadComponent: () => import('./pages/ai-content-manager/ai-content-manager.component').then(m => m.AiContentManagerComponent),
  },
  // [getPath(PathUnit.ClearCache)]: {
  //   loadComponent: () => import('./pages/clear-cache/clear-cache.component').then(m => m.ClearCacheComponent),
  // },
  [getPath(PathUnit.QualityDashboard)]: {
    loadComponent: environment.region === 'cn' ? () => import('./pages/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent)
    : () => import('./pages/admin/quality-dashboard/quality-dashboard.component').then(m => m.QualityDashboardComponent),
  },
  [getPath(PathUnit.JobLogAnalysis)]: {
    loadComponent: () => import('./pages/admin/job-log-analysis/job-log-analysis-list.component').then(m => m.JobLogAnalysisListComponent),
  },
  [getPath(PathUnit.LogQueryTop)]: {
    loadComponent: () => import('@/app/pages/log-query/log-query-top.component').then(m => m.LogQueryTopComponent),
  },
};

// Generate routes from AllAppLinks
function generateRoutesFromLinks(links: AppLink[]): Routes {
  const routes: Routes = [];
  function addRoutes(links: AppLink[]) {
    for (const link of links) {
      if (link.route && componentMap[link.route]) {
        // If roleAuthGuard is explicitly set to false, skip adding RoleGuardGuard
        if (link.roleAuthGuard === false) {
          routes.push({
            path: link.route,
            loadComponent: componentMap[link.route].loadComponent,
          });
          continue;
        } // else default to true
        routes.push({
          path: link.route,
          loadComponent: componentMap[link.route].loadComponent,
          canActivate: [RoleGuardGuard],
          data: { roles: link.roles }
        });
      }
      if (link.children) {
        addRoutes(link.children);
      }
    }
  }
  addRoutes(links);
  return routes;
}

export const routes: Routes = [
  {
    path: getPath(PathUnit.Login),
    component: LoginComponent,
    canActivate: [redirectToDashboardGuard]
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [authGuard], // Protect all child routes
    children: [
      {
        path: '',
        redirectTo: getPath(PathUnit.LandingPage),
        pathMatch: 'full',
      },
      {
        path: getPath(PathUnit.LandingPage),
        component: LandingPageComponent,
      },
      // Generated routes from AllAppLinks（Job Lookup → /job-status 由这里生成）
      ...generateRoutesFromLinks(AllAppLinks),
      // Additional static routes not in AllAppLinks
      { path: getPath(PathUnit.Unauthorized), component: UnauthorizedComponent },
      { path: getPath(PathUnit.Forbidden), component: ForbiddenComponent },
    ],
  },
  {
    path: getPath(PathUnit.Unsubscribe),
    component: UnsubscribeEmailNotifyComponent,
    // No auth guard, accessible to all
  },
  {
    path: '**',
    redirectTo: getPath(PathUnit.LandingPage),
  },
];
